'use client'

import { useEffect, useRef, useState } from 'react'

import { buildPrdIntakeAssistant } from './prd-intake-vapi-assistant'

const VAPI_KEY = process.env.NEXT_PUBLIC_VAPI_PUBLIC_KEY || null

// The interviewer's TTS pauses get transcribed as filler words even though
// the underlying text has none — same scrub as the z2h/careers voice hooks.
function polishInterviewerText(text) {
  return text
    .replace(/(^|[.?!—-]\s*)(uh|um)[,.]?\s+/gi, '$1')
    .replace(/\s+(uh|um)[,.]\s+/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/(^|[.?!]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase())
    .trim()
}

/**
 * Live Vapi voice call scoped to a SINGLE gap: the assistant asks that one
 * question, listens, confirms, and ends the call — the browser then syncs
 * the finalized turns to /api/z2h/prd/intake/voice-sync, which writes the
 * answer through the exact same path a typed answer takes
 * (src/lib/prd/intake.ts's writeGapAnswer). One call per gap keeps voice as
 * deterministic as the text flow (one gap per turn) — see
 * src/components/z2h/prd-intake-vapi-assistant.js for why this needs no
 * separate coverage-extraction pass the way the z2h/careers interview voice
 * flows do.
 */
export function usePrdIntakeVoice({ token, founderName, onAnswer, onError }) {
  const [callState, setCallState] = useState('idle') // idle|connecting|active|ending|finalizing
  const [assistantSpeaking, setAssistantSpeaking] = useState(false)
  const [liveCaption, setLiveCaption] = useState(null)
  const [muted, setMuted] = useState(false)

  const vapiRef = useRef(null)
  const turnsRef = useRef([])
  const lastFinalRef = useRef({ founder: '', interviewer: '' })
  const gapIndexRef = useRef(null)
  const gapQuestionRef = useRef('')
  const liveRef = useRef(false)
  const finalizedRef = useRef(false)
  const handlersRef = useRef({})
  handlersRef.current = { onAnswer, onError }

  const available = Boolean(VAPI_KEY && token)

  async function finalize() {
    if (finalizedRef.current) return
    finalizedRef.current = true
    setCallState('finalizing')
    const gapIndex = gapIndexRef.current
    const questionText = gapQuestionRef.current
    const turns = turnsRef.current
    try {
      const res = await fetch('/api/z2h/prd/intake/voice-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, gapIndex, questionText, turns, endCall: true }),
        keepalive: true,
      })
      const data = await res.json().catch(() => ({}))
      setCallState('idle')
      if (!res.ok || !data.ok) {
        // Same reload signal the text-chat path handles (Critical #1a review
        // fix): the gaps this call answered no longer exist as answered —
        // recover with a full reload rather than surfacing a confusing error
        // for what looks, from the founder's chair, like a saved answer.
        if (data.reload) {
          window.location.reload()
          return
        }
        handlersRef.current.onError?.(
          data.error || "We couldn't save your voice answer — try again, or answer by text.",
        )
        return
      }
      handlersRef.current.onAnswer?.({ gapIndex, answer: data.answer, allAnswered: data.allAnswered })
    } catch {
      setCallState('idle')
      handlersRef.current.onError?.("We couldn't save your voice answer — try again, or answer by text.")
    }
  }

  async function start({ gapIndex, gap, gapNumber, totalGaps }) {
    if (!available || callState !== 'idle' || gapIndex == null) return
    gapIndexRef.current = gapIndex
    gapQuestionRef.current = gap?.question || ''
    turnsRef.current = []
    lastFinalRef.current = { founder: '', interviewer: '' }
    liveRef.current = false
    finalizedRef.current = false
    setCallState('connecting')
    try {
      const { default: Vapi } = await import('@vapi-ai/web')
      const vapi = new Vapi(VAPI_KEY)
      vapiRef.current = vapi

      vapi.on('call-start', () => {
        liveRef.current = true
        setCallState('active')
        setMuted(false)
      })
      vapi.on('speech-start', () => setAssistantSpeaking(true))
      vapi.on('speech-end', () => setAssistantSpeaking(false))

      vapi.on('message', (m) => {
        if (m?.type !== 'transcript' || !m.transcript) return
        liveRef.current = true
        setCallState((s) => (s === 'connecting' ? 'active' : s))
        const role = m.role === 'assistant' ? 'interviewer' : 'founder'
        const clean = (t) => (role === 'interviewer' ? polishInterviewerText(t) : t.trim())
        if (m.transcriptType === 'partial') {
          setLiveCaption({ role, text: clean(m.transcript) })
          return
        }
        setLiveCaption(null)
        const text = clean(m.transcript)
        if (!text || lastFinalRef.current[role] === text) return
        lastFinalRef.current[role] = text
        const last = turnsRef.current[turnsRef.current.length - 1]
        if (last && last.role === role) {
          last.text = `${last.text} ${text}`.slice(0, 2000)
        } else {
          turnsRef.current.push({ role, text })
        }
      })

      vapi.on('call-end', async () => {
        setAssistantSpeaking(false)
        setLiveCaption(null)
        setMuted(false)
        await finalize()
      })

      vapi.on('error', async (e) => {
        console.error('Vapi error:', e)
        setAssistantSpeaking(false)
        setLiveCaption(null)
        setMuted(false)
        if (!liveRef.current) {
          setCallState('idle')
          handlersRef.current.onError?.(
            e?.errorMsg || e?.message || 'Could not start the voice call — you can answer by text.',
          )
          return
        }
        await finalize()
      })

      await vapi.start(buildPrdIntakeAssistant({ founderName, gap, gapNumber, totalGaps }))
    } catch (err) {
      setCallState('idle')
      handlersRef.current.onError?.(
        err?.message?.includes('Permission')
          ? 'Microphone access was blocked — allow it in your browser, or answer by text.'
          : err?.message || 'Could not start the voice call — you can answer by text.',
      )
    }
  }

  function stop() {
    setCallState('ending')
    try {
      vapiRef.current?.stop()
    } catch {
      setCallState('idle')
    }
  }

  function toggleMute() {
    const vapi = vapiRef.current
    if (!vapi) return
    try {
      const next = !vapi.isMuted()
      vapi.setMuted(next)
      setMuted(next)
    } catch {
      /* call not live yet */
    }
  }

  useEffect(
    () => () => {
      try {
        vapiRef.current?.stop()
      } catch {
        /* unmount cleanup */
      }
    },
    [],
  )

  return { available, callState, assistantSpeaking, liveCaption, muted, toggleMute, start, stop }
}
